'use client'

import * as React from 'react'
import { ExternalLink, Heart } from 'lucide-react'
import { cn, formatPrice } from '@/lib/utils'
import { api } from '@/lib/api'
import { buildPriceRows, derivePriceBreakdown, fmtCZK, perM2, pillBtnBase } from '@/components/roostr'
import { useI18n } from '@/lib/i18n'
import type { Listing } from '@/lib/types'

// Sticky "WHAT YOU'LL PAY" card — design detail.jsx:201-248, wired to the
// real price_breakdown. Rows beyond the design's rent/utilities/deposit trio
// (service charges, parking, internet — production data the design lacks)
// render in the same row style when present and count into the first-month
// total so the sum stays honest.
export interface PayCardProps {
  listing: Listing
  isSaved: boolean
  isSaving: boolean
  onToggleSave: () => void
}

export function PayCard({ listing, isSaved, isSaving, onToggleSave }: PayCardProps) {
  const { t } = useI18n()
  const fmt = (n: number) =>
    !listing.price_currency || listing.price_currency === 'CZK'
      ? fmtCZK(n)
      : formatPrice(n, listing.price_currency)

  // Listings carry no transaction_type — sale listings are detected by the
  // shared price heuristic and lose all monthly framing ('/mo', rent row,
  // first-month total). Unlike the liked/review surfaces, the deposit is
  // derived from months × price even for sale listings, and the extras count
  // into the first-month total (they render as rows here).
  const d = derivePriceBreakdown(listing, { deriveDepositWhenSale: true, extrasInFirstMonth: true })
  const isMonthly = d.monthly
  const depositLabel = d.depositMonths
    ? t('listingDetail.depositMonths', 'Deposit ({n} months)').replace('{n}', String(d.depositMonths))
    : t('listingDetail.deposit', 'Deposit')

  const rows = buildPriceRows(
    d,
    {
      monthlyRent: t('listingDetail.monthlyRent', 'Monthly rent'),
      utilities: t('listingDetail.utilities', 'Utilities'),
      deposit: depositLabel,
      serviceCharges: t('listingCard.serviceCharges', 'Service charges'),
      parking: t('listingCard.parking', 'Parking'),
      internet: t('listingDetail.internetTv', 'Internet / TV'),
    },
    fmt,
    { saleRows: true }
  )

  const firstMonth = d.firstMonth
  const hasBreakdownData = !!(d.utilities || d.serviceCharges || d.parking || d.internet || d.depositAmount)

  const marketplace = listing.marketplace || t('listingDetail.originalSite', 'original site')

  return (
    <div className="rounded-shape-xl border border-outline-variant bg-surface px-5 pb-5 pt-[18px] shadow-e1">
      <div className="text-[11px] font-bold uppercase tracking-[0.5px] text-ink-muted">
        {t('listingDetail.whatYoullPay', "What you'll pay")}
      </div>

      <div className="mt-2 flex items-end justify-between gap-2">
        <div className="flex items-baseline gap-1.5">
          <span className="font-mono text-[28px] font-bold text-primary">{fmt(listing.price)}</span>
          {isMonthly && <span className="text-[13px] text-ink-muted">{t('listingDetail.perMo', '/mo')}</span>}
        </div>
        {typeof listing.area === 'number' && listing.area > 0 && (
          <div className="text-right">
            <div className="text-[9.5px] font-bold uppercase tracking-[0.8px] text-ink-muted">
              {t('listingDetail.perM2', 'per m²')}
            </div>
            <div className="font-mono text-[13px] font-semibold">
              {fmt(perM2({ price: listing.price, area: listing.area }))}
            </div>
          </div>
        )}
      </div>

      {rows.length > 0 && (
        <div className="mt-[10px]">
          {rows.map(([label, value], i) => (
            <div
              key={label}
              className={cn(
                'flex items-center justify-between py-2',
                i < rows.length - 1 && 'border-b border-outline-variant'
              )}
            >
              <span className="text-[13px] text-ink-secondary">{label}</span>
              <span className="font-mono text-[13px] font-semibold">{value}</span>
            </div>
          ))}
        </div>
      )}

      {isMonthly && (
        <div className="mt-1 flex items-center justify-between border-t border-outline pt-[11px]">
          <span className="text-[13.5px] font-semibold">{t('listingDetail.totalFirstMonth', 'Total first month')}</span>
          <span className="font-mono text-[16px] font-bold text-primary">{fmt(firstMonth)}</span>
        </div>
      )}

      {listing.price_breakdown?.price_notes && (
        <p className="mt-2 text-[11px] text-ink-muted">{listing.price_breakdown.price_notes}</p>
      )}
      {hasBreakdownData && (
        <p className="mt-2 text-[10.5px] italic text-ink-muted">
          {t('listingDetail.aiCostHint', 'AI-analyzed from listing description. Actual costs may vary.')}
        </p>
      )}

      <a
        href={api.getRedirectUrl(listing.id)}
        target="_blank"
        rel="noopener noreferrer"
        className={cn(
          pillBtnBase,
          'mt-4 flex w-full justify-center bg-primary px-[18px] py-[13px] text-[14.5px] text-white shadow-e1'
        )}
      >
        <ExternalLink size={17} />
        {t('listingDetail.openOn', 'Open on')} {marketplace}
      </a>
      <button
        type="button"
        onClick={onToggleSave}
        disabled={isSaving}
        className={cn(
          pillBtnBase,
          'mt-2 flex w-full justify-center bg-primary-container px-[18px] py-3 text-[14px] text-on-primary-container',
          isSaving && 'opacity-60'
        )}
      >
        <Heart
          size={16}
          className={isSaved ? 'text-primary' : undefined}
          fill={isSaved ? 'currentColor' : 'none'}
        />
        {isSaved ? t('listingDetail.inLiked', 'In your Liked shortlist') : t('listingDetail.addToLiked', 'Add to Liked')}
      </button>
    </div>
  )
}
